/**
 * Browser notification utility for Runium app
 */

import { debugLog, debugWarn, debugError } from './debug';

class NotificationManager {
  constructor() {
    this.permission = null;
    this.isSupported = 'Notification' in window;
    this.serviceWorkerReg = null;
    this.init();
  }

  async init() {
    if (!this.isSupported) {
      debugWarn('Browser notifications are not supported');
      return;
    }

    this.permission = Notification.permission;
    
    // Wait for service worker to be ready
    if ('serviceWorker' in navigator) {
      try {
        this.serviceWorkerReg = await navigator.serviceWorker.ready;
        debugLog('🔧 Service Worker ready for notifications');
      } catch (error) {
        debugWarn('Service Worker not available:', error);
      }
    }
  }

  async requestPermission() {
    if (!this.isSupported) {
      return 'denied';
    }

    if (this.permission === 'granted') {
      return 'granted';
    }

    if (this.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    } catch (error) {
      debugError('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  async showNotification(title, options = {}) {
    debugLog('🔔 DEBUG: showNotification called:', { 
      title, 
      supported: this.isSupported, 
      permission: this.permission,
      hasServiceWorker: !!this.serviceWorkerReg
    });
    
    if (!this.isSupported) {
      debugWarn('Browser notifications are not supported');
      return false;
    }

    // Request permission if not already granted
    if (this.permission !== 'granted') {
      debugLog('🔔 DEBUG: Permission not granted, requesting...');
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        debugWarn('Notification permission not granted');
        return false;
      }
    }

    try {
      const defaultOptions = {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'runium-message',
        renotify: true,
        requireInteraction: false,
        ...options
      };

      debugLog('🔔 DEBUG: Creating notification with options:', defaultOptions);

      // Use Service Worker notification if available (better for mobile/PWA)
      if (this.serviceWorkerReg) {
        debugLog('🔔 DEBUG: Using Service Worker notification');
        await this.serviceWorkerReg.showNotification(title, defaultOptions);
        debugLog('🔔 DEBUG: Service Worker notification created successfully');
        return true;
      } else {
        // Fallback to regular notification
        debugLog('🔔 DEBUG: Using regular notification (fallback)');
        const notification = new Notification(title, defaultOptions);
        debugLog('🔔 DEBUG: Regular notification created successfully');

        // Auto-close after 5 seconds unless requireInteraction is true
        if (!defaultOptions.requireInteraction) {
          setTimeout(() => {
            notification.close();
          }, 5000);
        }

        // Handle click - focus the window
        notification.onclick = () => {
          window.focus();
          notification.close();
          
          // If there's a custom click handler, call it
          if (options.onClick) {
            options.onClick();
          }
        };

        return true;
      }
    } catch (error) {
      debugError('Error showing notification:', error);
      return false;
    }
  }

  showProactiveMessageNotification(messageContent) {
    debugLog('🔔 DEBUG: showProactiveMessageNotification called with content:', 
      typeof messageContent === 'string' ? messageContent.substring(0, 50) + '...' : messageContent);
    
    let title, body;
    
    if (typeof messageContent === 'string') {
      // Single message with content
      title = 'Your Coach';
      // Use actual message content, truncated if needed
      body = messageContent.length > 100 
        ? messageContent.substring(0, 97) + '...'
        : messageContent;
    } else if (typeof messageContent === 'number') {
      // Legacy: count of messages
      title = messageContent === 1 
        ? 'Your Coach'
        : 'Your Coach';
      body = messageContent === 1
        ? 'New message from your AI running coach'
        : `${messageContent} new messages from your AI running coach`;
    } else {
      // Fallback
      title = 'Your Coach';
      body = messageContent || 'New message from your AI running coach';
    }

    debugLog('🔔 DEBUG: Calling showNotification for proactive:', { title, body });
    return this.showNotification(title, {
      body,
      tag: 'proactive-message',
      icon: '/favicon.ico'
    });
  }

  showScheduledMessageNotification(messageContent) {
    debugLog('🔔 DEBUG: showScheduledMessageNotification called with content:', 
      typeof messageContent === 'string' ? messageContent.substring(0, 50) + '...' : messageContent);
    
    const title = 'Your Coach';
    
    // Use actual message content, truncated if needed
    const body = typeof messageContent === 'string' && messageContent.length > 100 
      ? messageContent.substring(0, 97) + '...'
      : messageContent;

    debugLog('🔔 DEBUG: Calling showNotification for scheduled:', { title, body });
    return this.showNotification(title, {
      body,
      tag: 'scheduled-message',
      icon: '/favicon.ico'
    });
  }

  showRegularMessageNotification(messageContent) {
    debugLog('🔔 DEBUG: showRegularMessageNotification called with content:', 
      typeof messageContent === 'string' ? messageContent.substring(0, 50) + '...' : messageContent);
    
    const title = 'Your Coach';
    
    // Use actual message content, truncated if needed
    const body = typeof messageContent === 'string' && messageContent.length > 100 
      ? messageContent.substring(0, 97) + '...'
      : messageContent;

    debugLog('🔔 DEBUG: Calling showNotification for regular:', { title, body });
    return this.showNotification(title, {
      body,
      tag: 'regular-message',
      icon: '/favicon.ico'
    });
  }

  showRunReminderNotification(runDetails) {
    debugLog('🔔 DEBUG: showRunReminderNotification called with details:', runDetails);
    
    const title = '🏃‍♀️ Training Reminder';
    const body = runDetails.body || 'You have training coming up soon!';

    debugLog('🔔 DEBUG: Calling showNotification for run reminder:', { title, body });
    return this.showNotification(title, {
      body,
      tag: 'run-reminder',
      icon: '/favicon.ico',
      requireInteraction: true, // Make run reminders more prominent
      actions: [
        {
          action: 'view',
          title: 'View Schedule'
        }
      ]
    });
  }

  showGeneralNotification(title, body, options = {}) {
    // Handle different notification types
    if (options.notificationType === 'run_reminder') {
      return this.showRunReminderNotification({ body });
    }
    
    return this.showNotification(title, {
      body,
      tag: 'general',
      ...options
    });
  }

  // Check if notifications are available and permission status
  getStatus() {
    return {
      supported: this.isSupported,
      permission: this.permission,
      available: this.isSupported && this.permission === 'granted',
      hasServiceWorker: !!this.serviceWorkerReg
    };
  }
}

// Create and export a singleton instance
const notificationManager = new NotificationManager();

export default notificationManager; 